import { type SetStateAction, type ReactNode, type Dispatch } from 'react';
import type { GlobalFilter } from '@/types';
import { FilterContext } from './useFilterContext';
import { getCurrentMonthDateRange } from '@/utils/dateUtils';
import usePersistentState from '@/hooks/contexts/usePersistentState';

export interface FilterContextType {
  filters: GlobalFilter;
  setFilters: Dispatch<SetStateAction<GlobalFilter>>;
}

const FilterProvider = ({
  children,
  initialState,
}: {
  children: ReactNode;
  initialState?: GlobalFilter;
}) => {
  const currentMonthDateRange = getCurrentMonthDateRange();
  const defaultFrom = currentMonthDateRange.from;
  const defaultTo = currentMonthDateRange.to;

  const defaultFilterState = {
    selectedShows: [],
    selectedPlatforms: undefined,
    dateRange: { from: defaultFrom, to: defaultTo },
  };

  const [filters, setFilters] = usePersistentState<GlobalFilter>(
    'filters',
    initialState || defaultFilterState
  );

  // Persisted filter blobs from older builds can omit selectedShows; treat missing as [].
  const normalizedFilters: GlobalFilter = {
    ...defaultFilterState,
    ...filters,
    selectedShows: filters.selectedShows ?? [],
    dateRange: filters.dateRange ?? defaultFilterState.dateRange,
  };

  return (
    <FilterContext.Provider value={{ filters: normalizedFilters, setFilters }}>
      {children}
    </FilterContext.Provider>
  );
};

export default FilterProvider;
